import { Option } from "@campgladiator/cgui-core.atoms.dropdown";
import {
  CouponDTO,
  PriceDTO,
  ProductDTO,
  SubscriptionDTO,
  UpgradeType,
} from "@campgladiator/cg-common.types.types";
import { centsToUSD } from "@campgladiator/cg-common.utility.numbers";
import {
  addDaysToISOstringDate,
  getFormattedDateWithOrdinalSuffix,
  getTotalDaysInMonth,
} from "@campgladiator/cg-common.utility.dates";

export const getActiveMembershipPrice = (prices: PriceDTO[]) => {
  const activePrice = prices.find(
    (price) => price.active && !price.deleted && price.amount
  );
  const priceToUSD = activePrice ? centsToUSD(activePrice.amount) : "";
  return priceToUSD;
};

type IsAllowedToChangeTrainerAndScheduleArgs = {
  currentSubscription: SubscriptionDTO;
  hasTrainerGrants: boolean;
  newTermLength: number;
  upgradeType: UpgradeType;
};

export const isAllowedToChangeTrainerAndSchedule = ({
  hasTrainerGrants,
  currentSubscription,
  upgradeType,
  newTermLength,
}: IsAllowedToChangeTrainerAndScheduleArgs): boolean => {
  if (hasTrainerGrants) {
    const { productSubType, termLength, contractStatistics } =
      currentSubscription;
    const { inContract } = contractStatistics;
    const isTermChanged = newTermLength !== termLength;
    const exceptionalScenarios =
      upgradeType === "SWAP" && productSubType === "IN_PERSON";

    if (exceptionalScenarios) {
      return inContract ? false : isTermChanged;
    }
    return true;
  }
  return true;
};

const getCouponDurationDescription = (duration: CouponDTO["duration"]) =>
  duration == "ONCE"
    ? "First Month"
    : duration === "FOREVER"
    ? "Forever"
    : "Repeating";

export const parseMemberships = (
  membership: ProductDTO[],
  upgradeType?: UpgradeType,
  currentSubscription?: SubscriptionDTO
): Option[] => {
  const options: Option[] = [];
  membership.forEach((product) => {
    const isDefault =
      upgradeType === "UPGRADE"
        ? product.termLength === currentSubscription.termLength
        : undefined;
    product.prices
      .filter((price) => price.active && !price.deleted)
      .forEach((price) => {
        options.push({
          itemId: `${product.stripeProductId}|${price.stripePriceId}`,
          text: `${product.stripeProductName} | ${centsToUSD(price.amount)}/mo`,
          type: "item",
          metadata: {
            ...product,
            prices: product.prices.filter(
              (p) => p.stripePriceId === price.stripePriceId
            ),
          },
          theme: "trainer",
          isDefault,
        });
      });
  });
  return options;
};

export const parseCoupons = (coupons: CouponDTO[]): Option[] =>
  coupons.map((coupon) => ({
    itemId: coupon.stripeCouponId,
    text: coupon.stripeCouponName,
    type: "item",
    metadata: coupon,
  }));

export const calculateDiscount = (
  price: number,
  percentageDiscount: CouponDTO["percentageDiscount"],
  duration: CouponDTO["duration"],
  termLength: string,
  amountOff?: CouponDTO["amountOff"]
) => {
  const discountAmount =
    amountOff && percentageDiscount === 0
      ? amountOff / 100
      : price * (percentageDiscount / 100);

  const newPrice = price - discountAmount;
  const days =
    duration === "ONCE" ? 30 : getTotalDaysInMonth(parseInt(termLength));
  const endDate = getFormattedDateWithOrdinalSuffix(
    addDaysToISOstringDate(new Date().toISOString(), days).toISOString()
  );

  return {
    discountAmount,
    newPrice,
    endDate,
    days,
  };
};
